<template>
  <div
    class="eds-sidebar"
    :class="[
      `sidebar-${color}`,
      {
        'sidebar-open': isSidebarOpen,
        'sidebar-closed': !isSidebarOpen,
      },
      sidebarClass,
      isSidebarOpen ? sidebarOpenedClass : sidebarClosedClass,
    ]"
    :style="[
      sidebarStyle,
      isSidebarOpen ? sidebarOpenedStyle : sidebarClosedStyle,
    ]"
  >
    <div
      class="sidebar-content"
      :class="[contentClass]"
      :style="contentStyle"
    >
      <div
        class="sidebar-section"
        :class="[sectionClass]"
        :style="sectionStyle"
      >
        <span
          v-if="isHeader && isSidebarOpen"
          class="eds-menu-header when-open"
          :class="[`menu-header-${color}`, headerClass]"
          :style="[headerStyle]"
        >
          <slot name="header" />
        </span>
        <span
          v-if="isHeader && !isSidebarOpen"
          class="eds-menu-header when-closed"
          :class="[`menu-header-${color}`, headerIconClass]"
          :style="[headerIconStyle]"
        >
          <slot name="headerIcon" />
        </span>
        <div
          v-show="isSidebarOpen"
          :class="[bodyClass]"
          :style="[bodyStyle]"
        >
          <slot name="body" />
        </div>
        <span
          v-for="(item, index) in items.filter(
            (item) => item.isDisplayed !== false
          )"
          :key="index"
          class="eds-menu-item"
          :class="[
            `menu-item-${color}${itemSelected(item.link)}`,
            { 'sidebar-open sidebar-closed': isSidebarOpen },
            menuItemClass,
            item.disabled ? 'disabler' : '',
          ]"
          style="cursor: pointer"
          :style="menuItemStyle"
          :title="item.title"
          @click="
            clickLink(item);
            if (item.link) {
              $emit('onMenuItemClick', `${item.link}`);
            }
          "
        >
          <span class="menu-item-icon material-icons">
            <slot :items="item.icon" />
          </span>
          <span class="menu-item-label when-open">
            <slot :items="item.label" />
          </span>
        </span>
      </div>
    </div>
    <div
      class="sidebar-controls"
      :class="controlClass"
      :style="controlStyle"
    >
      <span
        class="eds-sidebar-toggle sidebar-open ev-cursor-pointer"
        :class="[`sidebar-toggle-${color}`, toggleClass]"
        :style="[toggleStyle]"
        @click="onCollapseClick()"
      >
        <slot name="toggleIcon">
          <svg
            class="sidebar-toggle-icon when-open"
            :class="[toggleOpenClass]"
            :style="toggleOpenStyle"
            viewBox="0 0 24 24"
          >
            <path
              d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"
            />
          </svg>
          <svg
            class="sidebar-toggle-icon when-closed"
            :class="[toggleClosedClass]"
            :style="toggleClosedStyle"
            viewBox="0 0 24 24"
          >
            <path
              d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"
            />
          </svg>
        </slot>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue';
  import { useRoute } from 'vue-router';

  export default defineComponent({
    name: 'Sidebar',
    emits: ['onMenuItemClick'],
    props: {
      items: {
        type: Array as () => Array<any>,
        required: false,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      isHeader: {
        type: Boolean,
        required: false,
        default: false,
      },
      color: {
        type: String,
        required: false,
        default: 'blue',
        validator: (value: string) =>
          ['basic', 'blue', 'raspberry', 'gray'].includes(value),
      },
      sidebarClass: {
        type: String,
        required: false,
        default: '',
      },
      sidebarOpenedClass: {
        type: String,
        required: false,
        default: '',
      },
      sidebarClosedClass: {
        type: String,
        required: false,
        default: '',
      },
      sidebarStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      sidebarOpenedStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      sidebarClosedStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      contentClass: {
        type: String,
        required: false,
        default: '',
      },
      contentStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      sectionClass: {
        type: String,
        required: false,
        default: '',
      },
      sectionStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      menuItemClass: {
        type: String,
        required: false,
        default: '',
      },
      menuItemStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      headerClass: {
        type: String,
        required: false,
        default: '',
      },
      headerStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      headerIconClass: {
        type: String,
        required: false,
        default: '',
      },
      headerIconStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      bodyClass: {
        type: String,
        required: false,
        default: '',
      },
      bodyStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      controlClass: {
        type: String,
        required: false,
        default: '',
      },
      controlStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      toggleClass: {
        type: String,
        required: false,
        default: '',
      },
      toggleStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      toggleOpenClass: {
        type: String,
        required: false,
        default: '',
      },
      toggleOpenStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      toggleClosedClass: {
        type: String,
        required: false,
        default: '',
      },
      toggleClosedStyle: {
        type: Object,
        required: false,
        default: () => ({}),
      },
    },
    setup: () => {
      const route = useRoute();

      let isSidebarOpen = ref(true);
      let selected = ref(null);

      const onCollapseClick = (): void => {
        isSidebarOpen.value = !isSidebarOpen.value;
      };

      const clickLink = (item: any) => {
        if (item.extLink) {
          window.open(item.extLink, '_blank');
        } else {
          // isSidebarOpen.value = false;
          selected = item.link;
        }
      };

      const itemSelected = (link: string): string => {
        return link === route.name ? '-selected' : '';
      };

      return {
        isSidebarOpen,
        selected,
        onCollapseClick,
        itemSelected,
        clickLink,
      };
    },
  });
</script>
